import React, { useState, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export default function Gallery({ fotos = [], alt = "Foto" }) {
    const [index, setIndex] = useState(0);
    const [lightbox, setLightbox] = useState(false);

    const prev = useCallback(
        () => setIndex((i) => (i - 1 + fotos.length) % fotos.length),
        [fotos.length]
    );
    const next = useCallback(
        () => setIndex((i) => (i + 1) % fotos.length),
        [fotos.length]
    );

    useEffect(() => {
        if (!lightbox) return;
        const onKey = (e) => {
            if (e.key === "Escape") setLightbox(false);
            if (e.key === "ArrowLeft") prev();
            if (e.key === "ArrowRight") next();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightbox, prev, next]);

    if (!fotos.length) return null;

    return (
        <div data-testid="gallery" className="w-full">
            {/* Main image */}
            <div className="relative w-full aspect-[16/10] rounded-3xl overflow-hidden bg-cdb-sand group">
                <img
                    src={fotos[index]}
                    alt={`${alt} ${index + 1}`}
                    className="w-full h-full object-cover cursor-pointer transition-transform duration-700 hover:scale-105"
                    onClick={() => setLightbox(true)}
                    data-testid="gallery-main-image"
                />

                {/* Arrow buttons */}
                <button
                    data-testid="gallery-prev-btn"
                    onClick={prev}
                    aria-label="Foto anterior"
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-cdb-cream/90 backdrop-blur hover:bg-cdb-cream text-cdb-forest shadow-lg flex items-center justify-center transition-all hover:scale-110"
                >
                    <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                    data-testid="gallery-next-btn"
                    onClick={next}
                    aria-label="Próxima foto"
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-cdb-cream/90 backdrop-blur hover:bg-cdb-cream text-cdb-forest shadow-lg flex items-center justify-center transition-all hover:scale-110"
                >
                    <ChevronRight className="w-6 h-6" />
                </button>

                {/* Counter */}
                <div className="absolute bottom-4 right-4 bg-cdb-forest/80 text-cdb-cream text-xs font-bold px-3 py-1.5 rounded-full backdrop-blur">
                    {index + 1} / {fotos.length}
                </div>
            </div>

            {/* Thumbnails */}
            <div className="mt-4 grid grid-cols-5 sm:grid-cols-8 gap-2">
                {fotos.map((src, i) => (
                    <button
                        key={src + i}
                        data-testid={`gallery-thumb-${i}`}
                        onClick={() => setIndex(i)}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                            i === index
                                ? "border-cdb-forest scale-95"
                                : "border-transparent hover:border-cdb-gold opacity-80 hover:opacity-100"
                        }`}
                    >
                        <img src={src} alt={`${alt} thumb ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                ))}
            </div>

            {/* Lightbox */}
            {lightbox && (
                <div
                    data-testid="gallery-lightbox"
                    className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
                    onClick={() => setLightbox(false)}
                >
                    <button
                        data-testid="gallery-lightbox-close"
                        onClick={() => setLightbox(false)}
                        className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                        aria-label="Fechar"
                    >
                        <X className="w-6 h-6" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            prev();
                        }}
                        className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                        aria-label="Anterior"
                    >
                        <ChevronLeft className="w-7 h-7" />
                    </button>
                    <img
                        src={fotos[index]}
                        alt={`${alt} ampliada`}
                        className="max-h-[88vh] max-w-[92vw] object-contain rounded-xl"
                        onClick={(e) => e.stopPropagation()}
                    />
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            next();
                        }}
                        className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                        aria-label="Próxima"
                    >
                        <ChevronRight className="w-7 h-7" />
                    </button>
                </div>
            )}
        </div>
    );
}
